import type { FC } from 'react';
import { DrawerSection } from '@components/DrawerSection';
import { Show } from '@components/Show';
import Toggle from '@components/Toggle';
import type { BlockEditFormState } from '@ContractBuilder/modules/block-edit';

import { useBlockPanelContent } from '../hooks/useBlockPanelContent';
import type { ToggleFormField } from '../types';
import type { PanelMode } from '../utils/get-panel-mode';

import { DrawerCollapsibleGroup } from './DrawerCollapsibleGroup';

interface DrawerPanelPermissionsProps {
  formValues?: BlockEditFormState;
  isBlockType: boolean;
  isSuperAdminUser: boolean;
  mode: PanelMode;
  toggleField: ToggleFormField;
}

export const DrawerPanelPermissions: FC<DrawerPanelPermissionsProps> = ({
  formValues,
  isBlockType,
  isSuperAdminUser,
  mode,
  toggleField,
}) => {
  const { isAdminView, isTemplate } = mode;
  const { getContent } = useBlockPanelContent(isBlockType);

  const canDeleteOnTemplate = formValues?.canDeleteOnTemplate ?? true;
  const canEditOnTemplate = formValues?.canEditOnTemplate ?? true;
  const canEdit = formValues?.canEdit ?? true;
  const canDelete = formValues?.canDelete ?? true;

  return (
    <DrawerCollapsibleGroup title={getContent('groups.permissions')} id="permissions">
      <Show when={isAdminView && isSuperAdminUser}>
        <Toggle
          labelText={getContent('fields.settings.superAdminEdit.label')}
          value={!canEditOnTemplate}
          onClick={toggleField('canEditOnTemplate', canEditOnTemplate)}
          data-testid="toggle-can-edit-by-superadmin"
        />
      </Show>
      <Show when={isTemplate && isSuperAdminUser}>
        <DrawerSection title={getContent('fields.templatePermissions.label')}>
          <Toggle
            labelText={getContent('fields.templatePermissions.canDelete.label')}
            value={canDeleteOnTemplate}
            onClick={toggleField('canDeleteOnTemplate', canDeleteOnTemplate)}
            data-testid="toggle-can-delete-from-template"
          />
        </DrawerSection>
      </Show>
      <Show when={isAdminView}>
        <DrawerSection title={getContent('fields.submissionPermissions.label')}>
          <Toggle
            labelText={getContent('fields.submissionPermissions.canEdit.label')}
            value={canEdit}
            onClick={toggleField('canEdit', canEdit)}
            data-testid="toggle-can-edit"
          />
          <Toggle
            labelText={getContent('fields.submissionPermissions.canDelete.label')}
            value={canDelete}
            onClick={toggleField('canDelete', canDelete)}
            data-testid="toggle-can-delete"
          />
        </DrawerSection>
      </Show>
    </DrawerCollapsibleGroup>
  );
};
